import { binarySearchIncludes } from "utils/arrays";
import { type VecRGBA } from "utils/color";
import { SelectionSet } from "utils/selectionSet";

/**
 * Per-mode color overrides: a handful of color buckets, each a SelectionSet of
 * ids plus a shared frozen vec. Lookup is membership across those buckets
 * (typically < 20). There is no per-object `Record` — that was ~32 B/id on top
 * of the reverse arrays.
 */
export type PaletteBucket = {
    ids: SelectionSet;
    vec: VecRGBA;
};

export type ObjectColorPalette = Record<string, PaletteBucket>;

export function emptyObjectColorPalette(): ObjectColorPalette {
    return {};
}

export type PaletteEntry = {
    /** Color bucket key, `r|g|b|a`. */
    key: string;
    /** Canonical frozen RGBA vec, shared by every object with this color. */
    vec: VecRGBA;
    objectIds: number[];
};

/** RGBA override for `objectId`, or undefined if the object has none in this palette. */
export function getPaletteColor(palette: ObjectColorPalette | undefined, objectId: number): VecRGBA | undefined {
    if (!palette) {
        return undefined;
    }
    
    for (const key in palette) {
        const bucket = palette[key];
        if (bucket.ids.has(objectId)) {
            return bucket.vec;
        }
    }

    return undefined;
}

function colorEquals(a: number[], b: number[]): boolean {
    return a === b || (a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3]);
}

/**
 * Sorted `a` minus sorted `b`. Returns `a` when nothing was removed.
 *
 * Two-pointer merge rather than {@link SelectionSet.difference}: both sides are
 * already sorted, so this is O(n+m) with no bitset. `difference` walks `this`
 * and calls `other.has()`, which allocates a (maxId/8)-byte bitset once `other`
 * is large — the wrong tradeoff for a 150k-id federated clear.
 */
function subtractSorted(a: Uint32Array, b: Uint32Array): Uint32Array {
    if (b.length === 0 || a.length === 0) {
        return a;
    }
    
    const out = new Uint32Array(a.length);
    let ai = 0;
    let bi = 0;
    let count = 0;
    while (ai < a.length) {
        const av = a[ai];
        while (bi < b.length && b[bi] < av) {
            bi++;
        }
        if (bi < b.length && b[bi] === av) {
            ai++;
            bi++;
            continue;
        }
        out[count++] = av;
        ai++;
    }
    
    if (count === a.length) {
        return a;
    }

    return count === 0 ? new Uint32Array(0) : out.slice(0, count);
}

function subtractIds(a: SelectionSet, b: SelectionSet): SelectionSet {
    const next = subtractSorted(a.sortedIds, b.sortedIds);
    return next === a.sortedIds ? a : SelectionSet.fromSortedIds(next);
}

function findOldKeys(base: ObjectColorPalette, incomingIds: ReadonlySet<number>): Map<number, string> {
    const oldKeyById = new Map<number, string>();
    const incomingSize = incomingIds.size;
    for (const key in base) {
        const ids = base[key].ids.sortedIds;
        // Binary-search each incoming id when the batch is small vs the bucket;
        // otherwise scan the bucket once (bulk paint of a federated scene).
        // Probe via sortedIds rather than SelectionSet.has so a large bucket
        // does not allocate a bitset during apply.
        const logN = 32 - Math.clz32(ids.length | 1);
        if (incomingSize * logN < ids.length) {
            for (const id of incomingIds) {
                if (binarySearchIncludes(ids, id)) {
                    oldKeyById.set(id, key);
                }
            }
        } else {
            for (let i = 0; i < ids.length; i++) {
                const id = ids[i];
                if (incomingIds.has(id)) {
                    oldKeyById.set(id, key);
                }
            }
        }
    }

    return oldKeyById;
}

/**
 * Applies palette entries in a single pass. Last entry wins when an object appears
 * in several. Returns null when nothing changes so callers keep previous references.
 *
 * Avoids per-object copies (spread, filter, includes) — they go quadratic and OOM
 * at six-figure object counts.
 */
export function applyPaletteEntries(base: ObjectColorPalette, entries: PaletteEntry[]): ObjectColorPalette | null {
    const incomingIds = new Set<number>();
    for (const { objectIds } of entries) {
        for (const id of objectIds) {
            incomingIds.add(id);
        }
    }
    if (incomingIds.size === 0) {
        return null;
    }

    const oldKeyById = findOldKeys(base, incomingIds);

    // Reuse as "already applied" and walk entries last-to-first so the last
    // occurrence of each id wins, without a per-id {key, vec} map.
    incomingIds.clear();
    const addedByKey = new Map<string, { vec: VecRGBA; ids: number[] }>();
    const removedByKey = new Map<string, number[]>();
    let anyChange = false;
    for (let e = entries.length - 1; e >= 0; e--) {
        const { key, vec, objectIds } = entries[e];
        for (const id of objectIds) {
            if (incomingIds.has(id)) {
                continue;
            }
            incomingIds.add(id);

            const oldKey = oldKeyById.get(id);
            const oldVec = oldKey !== undefined ? base[oldKey].vec : undefined;
            if (oldVec && colorEquals(oldVec, vec)) {
                continue;
            }
            anyChange = true;
            if (oldKey !== undefined) {
                let removed = removedByKey.get(oldKey);
                if (!removed) {
                    removed = [];
                    removedByKey.set(oldKey, removed);
                }
                removed.push(id);
            }
            let added = addedByKey.get(key);
            if (!added) {
                added = { vec, ids: [] };
                addedByKey.set(key, added);
            }
            added.ids.push(id);
        }
    }

    if (!anyChange) {
        return null;
    }

    const next: ObjectColorPalette = Object.assign({}, base);
    const dirtyKeys = new Set<string>();
    for (const key of removedByKey.keys()) {
        dirtyKeys.add(key);
    }

    for (const key of addedByKey.keys()) {
        dirtyKeys.add(key);
    }

    for (const key of dirtyKeys) {
        const removed = removedByKey.get(key);
        const added = addedByKey.get(key);
        let ids = next[key]?.ids;
        if (removed && ids) {
            ids = subtractIds(ids, SelectionSet.fromIds(removed));
        }
        if (added) {
            ids = ids ? ids.withAdded(added.ids) : SelectionSet.fromIds(added.ids);
            if (ids.size === 0) {
                delete next[key];
            } else {
                next[key] = { ids, vec: added.vec };
            }
        } else if (!ids || ids.size === 0) {
            delete next[key];
        } else {
            next[key] = { ids, vec: next[key].vec };
        }
    }

    return next;
}

/**
 * Removes color overrides for the given object ids. Returns null when none of
 * the ids have an override so callers keep previous state references.
 */
export function clearPaletteObjects(base: ObjectColorPalette, objectIds: number[]): ObjectColorPalette | null {
    if (objectIds.length === 0) {
        return null;
    }

    const toClear = SelectionSet.fromIds(objectIds);
    let anyChange = false;
    const next: ObjectColorPalette = Object.assign({}, base);
    for (const key in base) {
        const prev = base[key];
        const ids = subtractIds(prev.ids, toClear);
        if (ids === prev.ids) {
            continue;
        }
        anyChange = true;
        if (ids.size === 0) {
            delete next[key];
        } else {
            next[key] = { ids, vec: prev.vec };
        }
    }

    return anyChange ? next : null;
}
